import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { SortingState } from "@tanstack/react-table";
import {
  Add,
  Cards,
  Table as TableIcon,
  Tasks as TasksIcon
} from "@dreamhub/designsystem/icons";
import Text from "@/components/ui/text";
import { IconButton } from "@/components/ui/icon-button";
import LogoIconContainer from "@/components/ui/logo-icon-container";
import Tabs, { tabsPropsType } from "@/components/controls/Tabs";
import LayoutTabs from "@/components/controls/LayoutTabs";
import ToolbarActions from "@/components/controls/ToolbarActions/ToolbarActions";
import TasksTable from "@/components/layouts/TasksFocus/TasksTable";
import TaskCardsGrid from "@/pages/tasks/components/TaskCardsGrid";
import TaskModal from "@/components/layouts/TasksDrawer/TaskModal";
import ConfirmationModal from "@/components/layouts/ConfirmationModal";
import EmptyTasksState from "@/components/layouts/TasksDrawer/EmptyTasksState";
import SuccessToast from "@/components/controls/Task/SuccessToast";
import { createModalStateSetter } from "@/entities/modals/helpers";
import {
  useDeleteTaskMutation,
  useFilteredTasks,
  useRefreshFilteredTasks,
  useRemoveTaskFromLists,
  useUndoDeleteTaskMutation
} from "@/entities/tasks/hooks";
import CounterBadge from "@/components/ui/counter-badge";
import { useTasksListData } from "@/entities/tasks/useTasksListData";
import {
  DeleteTaskModalState,
  TaskModalState,
  TaskState,
  TaskTabs
} from "@/entities/tasks/types";
import { useUsersMe } from "@/entities/users/hooks";
import { useUserFilterValues } from "@/entities/users/useUserSelect";
import { EmptyFilters, Filters } from "@/services/api/types";
import { ColumnData } from "@/entities/dataTables/types";
import { getDefaultTaskColumnsState } from "@/entities/tasks/helpers";
import { DataTableFilter } from "@/components/controls/DataTableFilters/DataTableFilters";
import { EntityType } from "@/services/hooks/entities/entityTypes";

const TRANSLATION_KEY = "focus.tasks";

enum TasksView {
  TABLE = "table",
  CARDS = "cards"
}

const TasksPage: React.FC = () => {
  const { t } = useTranslation();
  const userData = useUsersMe().data;
  const userFilterValues = useUserFilterValues();

  const tabsProps: tabsPropsType = {
    options: {
      [TaskState.OPEN]: t(`${TRANSLATION_KEY}.tabs.${TaskState.OPEN}`),
      [TaskState.COMPLETED]: t(`${TRANSLATION_KEY}.tabs.${TaskState.COMPLETED}`)
    },
    defaultValue: TaskState.OPEN
  };

  const layoutTabsProps: tabsPropsType = {
    options: {
      [TaskTabs.ALL_TASKS]: t(
        `${TRANSLATION_KEY}.layoutTabs.${TaskTabs.ALL_TASKS}`
      ),
      [TaskTabs.ASSIGNED_TO_ME]: t(
        `${TRANSLATION_KEY}.layoutTabs.${TaskTabs.ASSIGNED_TO_ME}`
      ),
      [TaskTabs.ASSIGNED_BY_ME]: t(
        `${TRANSLATION_KEY}.layoutTabs.${TaskTabs.ASSIGNED_BY_ME}`
      )
    },
    defaultValue: TaskTabs.ALL_TASKS
  };

  const [selectedTab, setSelectedTab] = useState(tabsProps.defaultValue);
  const [selectedLayoutTab, setSelectedLayoutTab] = useState(
    layoutTabsProps.defaultValue
  );
  const [selectedView, setSelectedView] = useState<string>(TasksView.TABLE);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState<Filters>(EmptyFilters);
  const [columnsStateByTab, setColumnsStateByTab] = useState<
    Record<string, ColumnData[]>
  >({});
  const columnsState = columnsStateByTab[selectedTab as string] ?? [];
  const setColumnsState = useCallback<
    React.Dispatch<React.SetStateAction<ColumnData[]>>
  >(
    (value) =>
      setColumnsStateByTab((prev) => {
        const tabKey = selectedTab as string;
        const current = prev[tabKey] ?? [];
        return {
          ...prev,
          [tabKey]: typeof value === "function" ? value(current) : value
        };
      }),
    [selectedTab]
  );

  const [sorting, onSortingChange] = useState<SortingState>([
    { id: "dueDate", desc: false }
  ]);

  const deleteTask = useDeleteTaskMutation();
  const undoDeleteTask = useUndoDeleteTaskMutation();

  const [taskModalState, setTaskModalState] = useState<TaskModalState>({
    isOpen: false,
    isEditMode: false,
    editTaskId: null
  });
  const [deleteTaskModalState, setDeleteTaskModalState] =
    useState<DeleteTaskModalState>({
      isOpen: false,
      task: { id: null, name: null }
    });

  const baseFiltersForTotals = useMemo(
    () => ({
      ...EmptyFilters,
      isCompleted: selectedTab === TaskState.COMPLETED
    }),
    [selectedTab]
  );

  const allTasksFilters = useMemo(
    () => ({
      ...baseFiltersForTotals,
      ...filters,
      $or: [{ assigneeId: userData?.id }, { createdBy: userData?.id }]
    }),
    [baseFiltersForTotals, filters, userData?.id]
  );

  const assignedToMeFilters = useMemo(
    () => ({
      ...baseFiltersForTotals,
      ...filters,
      assigneeId: userData?.id,
      createdBy: undefined
    }),
    [baseFiltersForTotals, filters, userData?.id]
  );

  const assignedByMeFilters = useMemo(
    () => ({
      ...baseFiltersForTotals,
      ...filters,
      assigneeId: { ne: userData?.id },
      createdBy: userData?.id
    }),
    [baseFiltersForTotals, filters, userData?.id]
  );

  const isCurrentUserResolved = Boolean(userData?.id);

  const allAssignedToMeFilters = useMemo(
    () =>
      isCurrentUserResolved
        ? { ...EmptyFilters, assigneeId: userData?.id, createdBy: undefined }
        : EmptyFilters,
    [isCurrentUserResolved, userData?.id]
  );

  const allAssignedByMeFilters = useMemo(
    () =>
      isCurrentUserResolved
        ? {
            ...EmptyFilters,
            assigneeId: { ne: userData?.id },
            createdBy: userData?.id
          }
        : EmptyFilters,
    [isCurrentUserResolved, userData?.id]
  );

  const refreshTasks = useRefreshFilteredTasks();
  const removeTaskFromLists = useRemoveTaskFromLists();

  const setIsTaskModalOpen = createModalStateSetter(setTaskModalState);
  const setIsDeleteTaskModalOpen = createModalStateSetter(
    setDeleteTaskModalState
  );

  const { total: allTasksTotal = 0 } = useFilteredTasks(
    allTasksFilters,
    searchTerm ? { term: searchTerm } : undefined
  );

  const { total: assignedToMeTotal = 0 } = useFilteredTasks(
    assignedToMeFilters,
    searchTerm ? { term: searchTerm } : undefined
  );

  const { total: assignedByMeTotal = 0 } = useFilteredTasks(
    assignedByMeFilters,
    searchTerm ? { term: searchTerm } : undefined
  );

  const { total: allAssignedToMeTotal = 0, isLoading: allAssignedToMeLoading } =
    useFilteredTasks(
      allAssignedToMeFilters,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      isCurrentUserResolved
    );

  const { total: allAssignedByMeTotal = 0, isLoading: allAssignedByMeLoading } =
    useFilteredTasks(
      allAssignedByMeFilters,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      isCurrentUserResolved
    );

  const {
    tasks,
    hasNextPage,
    isLoading: areTasksLoading,
    isFetchingNextPage,
    users,
    taskAnimationStates,
    handleLoadMore,
    handleCheckedChange
  } = useTasksListData({
    selectedLayoutTab: selectedLayoutTab as string,
    selectedTab: selectedTab as string,
    allTasksFilters,
    assignedToMeFilters,
    assignedByMeFilters,
    searchTerm,
    sorting,
    enabled: selectedView === TasksView.CARDS
  });

  const enhancedLayoutTabsProps: tabsPropsType = useMemo(
    () => ({
      ...layoutTabsProps,
      value: selectedLayoutTab,
      options: Object.entries(layoutTabsProps.options).reduce(
        (acc, [key, value]) => {
          let total = 0;
          if (key === TaskTabs.ALL_TASKS) {
            total = allTasksTotal;
          } else if (key === TaskTabs.ASSIGNED_TO_ME) {
            total = assignedToMeTotal;
          } else {
            total = assignedByMeTotal;
          }
          acc[key] = (
            <div className="flex items-center gap-[6px]">
              <span>{value}</span>
              {total > 0 && <CounterBadge count={total} />}
            </div>
          );
          return acc;
        },
        {} as { [key: string]: string | JSX.Element }
      )
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedLayoutTab, allTasksTotal, assignedToMeTotal, assignedByMeTotal, t]
  );

  const shouldShowEmptyState = useMemo(
    () =>
      isCurrentUserResolved &&
      !allAssignedToMeLoading &&
      !allAssignedByMeLoading &&
      allAssignedToMeTotal + allAssignedByMeTotal === 0,
    [
      isCurrentUserResolved,
      allAssignedToMeLoading,
      allAssignedByMeLoading,
      allAssignedToMeTotal,
      allAssignedByMeTotal
    ]
  );

  const handleUndoDeleteTask = (taskId: string) => {
    undoDeleteTask.mutate(
      { pathParams: { taskId } },
      { onSuccess: () => refreshTasks() }
    );
  };

  const handleDeleteTask = () => {
    const taskId = deleteTaskModalState?.task?.id;
    if (!taskId) {
      setIsDeleteTaskModalOpen(false);
      return;
    }
    deleteTask.mutate(
      { pathParams: { taskId } },
      {
        onSuccess: () => {
          setDeleteTaskModalState({
            isOpen: false,
            task: { id: null, name: null }
          });
          removeTaskFromLists(taskId);
          toast.success(
            <SuccessToast
              undoLabel={t("undo")}
              successTitle={t(`${TRANSLATION_KEY}.taskDeleted`)}
              handleUndo={() => handleUndoDeleteTask(taskId)}
              refreshTasks={refreshTasks}
            />,
            { duration: 3000 }
          );
        }
      }
    );
  };

  const defaultColumnsState = useMemo(
    () => getDefaultTaskColumnsState(t, selectedTab as string),
    [t, selectedTab]
  );

  const filterOptions = useMemo<DataTableFilter[]>(
    () => [
      {
        key: "assigneeId",
        filterType: {
          type: "map",
          values: userFilterValues,
          showAvatar: true,
          sort: true,
          showSearch: true,
          entity: EntityType.USER
        }
      },
      {
        key: "isHighPriority",
        filterType: {
          type: "boolean",
          label: t(`${TRANSLATION_KEY}.filters.isHighPriorityLabel`),
          trueLabel: `${TRANSLATION_KEY}.filters.isHighPriorityTrue`,
          falseLabel: `${TRANSLATION_KEY}.filters.isHighPriorityFalse`
        }
      },
      {
        key: "dueDate",
        filterType: { type: "date" }
      }
    ],
    [userFilterValues, t]
  );

  const showAsOptions = useMemo(
    () => [
      { value: TasksView.TABLE, label: <TableIcon /> },
      { value: TasksView.CARDS, label: <Cards /> }
    ],
    []
  );

  return (
    <div data-test="tasks-page" className="h-full">
      <div
        data-test="tasks-page-header"
        className="flex justify-between p-4 pb-2 h-14"
      >
        <div data-test="tasks-page-title" className="flex items-center gap-[10px]">
          <LogoIconContainer
            icon={<TasksIcon />}
            index={-1}
            className="size-8"
            iconSize="5"
          />
          <Text as="div" variant="lgSubtitleH1" className="p-1">
            {t(`${TRANSLATION_KEY}.pageTitle`)}
          </Text>
        </div>
        <div data-test="tasks-page-actions" className="flex items-center gap-3">
          <Tabs
            {...tabsProps}
            value={selectedTab}
            valueSelected={(value) => setSelectedTab(value as string)}
          />
          <IconButton
            data-test="tasks-page-create-button"
            onClick={() =>
              setTaskModalState({
                editTaskId: null,
                isEditMode: false,
                isOpen: true
              })
            }
            size="lg"
          >
            <Add />
          </IconButton>
        </div>
      </div>
      {!shouldShowEmptyState && (
        <LayoutTabs
          {...enhancedLayoutTabsProps}
          valueSelected={(newTabValue) => setSelectedLayoutTab(newTabValue)}
        />
      )}
      {shouldShowEmptyState ? (
        <div className="flex w-full h-full justify-center items-center">
          <EmptyTasksState />
        </div>
      ) : (
        <>
          <ToolbarActions
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            filters={filters}
            setFilters={setFilters}
            filterOptions={filterOptions}
            sorting={sorting}
            onSortingChange={onSortingChange}
            defaultSortByValue="dueDate"
            columnsState={columnsState}
            setColumnsState={setColumnsState}
            defaultColumnsState={defaultColumnsState}
            // Tasks locks its leading checkmark and title columns, which the column list never shows.
            lockFirstColumn={false}
            onColumnsChange={setColumnsState}
            showAsValue={selectedView}
            onShowAsChange={setSelectedView}
            showAsOptions={showAsOptions}
            tableId="tasks-page-table"
            className="p-2 pr-4"
            baseFilters={EmptyFilters}
          />
          {selectedView === TasksView.TABLE ? (
            <TasksTable
              selectedLayoutTab={selectedLayoutTab as string}
              selectedTab={selectedTab as string}
              allTasksFilters={allTasksFilters}
              assignedToMeFilters={assignedToMeFilters}
              assignedByMeFilters={assignedByMeFilters}
              searchTerm={searchTerm}
              userId={userData?.id}
              setTaskModalState={setTaskModalState}
              setDeleteTaskModalState={setDeleteTaskModalState}
              sorting={sorting}
              onSortingChange={onSortingChange}
              columnsState={columnsState}
              tableId="tasks-page-table"
              height="calc(var(--app-content-h) - 11rem)"
            />
          ) : (
            <TaskCardsGrid
              tasks={tasks}
              selectedTab={selectedTab as string}
              userId={userData?.id}
              users={users}
              taskAnimationStates={taskAnimationStates}
              onCheckedChange={handleCheckedChange}
              setTaskModalState={setTaskModalState}
              setDeleteTaskModalState={setDeleteTaskModalState}
              isSearching={!!searchTerm}
              isLoading={areTasksLoading}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              onLoadMore={handleLoadMore}
              height="calc(var(--app-content-h) - 11rem)"
            />
          )}
        </>
      )}
      <TaskModal
        isOpen={taskModalState.isOpen}
        setIsOpen={setIsTaskModalOpen}
        isEditMode={taskModalState.isEditMode}
        editTaskId={taskModalState.editTaskId}
        editTaskData={taskModalState.editTaskData}
      />
      <ConfirmationModal
        isOpen={deleteTaskModalState.isOpen}
        setIsOpen={setIsDeleteTaskModalOpen}
        title={t(`${TRANSLATION_KEY}.deleteConfirmation.title`)}
        confirmationText={t(
          `${TRANSLATION_KEY}.deleteConfirmation.confirmation`,
          { taskName: deleteTaskModalState.task.name }
        )}
        handleSubmit={handleDeleteTask}
      />
    </div>
  );
};

export default TasksPage;
